<template>
  <div class="pt-panel pumping-info-panel">
    <div class="pt-header">
      <div class="pt-header-text">
        <div class="pt-kicker">工程信息</div>
        <h2 class="pt-title">{{ oneTitle }}</h2>
        <p class="pt-subtitle">泵站工程参数 · 设计指标 · 实时监测</p>
      </div>
      <button class="pt-close" @click="closeClick">&times;</button>
    </div>

    <div class="pt-tabs">
      <div
          v-for="(tab, idx) in tabs"
          :key="idx"
          class="pt-tab"
          :class="{ active: activeTab === tab }"
          @click="onTabChange(tab)"
      >
        {{ tab }}
      </div>
    </div>

    <!-- 工程参数 -->
    <div v-if="activeTab === '工程参数'" class="pt-flex-table">
      <div class="pt-flex-table-header">
        <div class="pt-flex-table-cell">项目</div>
        <div class="pt-flex-table-cell">内容</div>
      </div>
      <div
          class="pt-flex-table-row"
          v-for="(item, index) in parkInfo"
          :key="index"
      >
        <div class="pt-flex-table-cell">{{ item.label }}</div>
        <div class="pt-flex-table-cell">{{ item.value }}</div>
      </div>
    </div>

    <!-- 实时监测 -->
    <div v-if="activeTab === '实时监测'" class="ts-section">
      <div class="ts-metrics-row">
        <div class="ts-metric-card" v-for="(m, i) in tsMetrics" :key="'m'+i">
          <span class="ts-metric-label">{{ m.label }}</span>
          <div class="ts-metric-val-row">
            <span class="ts-metric-value" :class="m.cls">{{ m.value }}</span>
            <span class="ts-metric-unit">{{ m.unit }}</span>
          </div>
        </div>
      </div>

      <div class="ts-chart-group">
        <div class="ts-chart-card" v-for="(c, ci) in chartConfigs" :key="'c'+ci">
          <div class="ts-chart-title">{{ c.title }}</div>
          <div class="ts-chart-canvas" :ref="'chart' + ci"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: "pumpingInfo",
  props: {
    info: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      oneTitle: "",
      tabs: ["工程参数", "实时监测"],
      activeTab: "工程参数",
      parkInfo: [],
      tsData: null,
      tsMetrics: [],
      chartConfigs: [],
    };
  },

  created() {
    this.echartInstances = [];
  },

  mounted() {
    this.updateData();
    this._resizeHandler = () => {
      this.echartInstances.forEach(c => { try { c.resize(); } catch(e) {} });
    };
    window.addEventListener('resize', this._resizeHandler);
  },

  watch: {
    info: {
      handler(val) {
        this.oneTitle = val.name || "泵站信息";
        this.updateData();
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    this._destroyCharts();
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  },

  methods: {
    onTabChange(tab) {
      this.activeTab = tab;
      if (tab === '实时监测') {
        this.$nextTick(() => {
          setTimeout(() => this._renderCharts(), 200);
        });
      }
    },

    updateData() {
      this.parkInfo = [
        { label: "工程类型", value: "提水泵站" },
        { label: "建设性质", value: "改建工程" },
        { label: "设计流量", value: "2.5 m³/s" },
        { label: "装机台数", value: "3 台（2 用 1 备）" },
        { label: "单机流量", value: "1.25 m³/s" },
        { label: "装机总功率", value: "450 kW" },
        { label: "控制方式", value: "自动化控制+远程监控" },
      ];

      // 生成泵站运行时间序列（24h × 15min = 96 点）
      this.tsData = this._generatePumpTimeSeries();

      const ts = this.tsData;
      const lastIdx = ts.times.length - 1;
      const curFlow = ts.flow[lastIdx];
      const curPower = ts.power[lastIdx];
      const curHead = ts.head[lastIdx];
      const totalWater = ts.flow.reduce((a, b) => a + b, 0) * 0.25 * 0.36; // 万 m³

      this.tsMetrics = [
        { label: '当前流量', value: curFlow.toFixed(2), unit: 'm³/s', cls: 'st-green' },
        { label: '当前功率', value: curPower.toFixed(1), unit: 'kW', cls: '' },
        { label: '当前扬程', value: curHead.toFixed(2), unit: 'm', cls: '' },
        { label: '今日供水量', value: totalWater.toFixed(2), unit: '万 m³', cls: 'st-green' },
      ];

      this.chartConfigs = [
        { title: '流量曲线 (m³/s)', data: ts.flow, color: '#38bdf8', area: true },
        { title: '功率曲线 (kW)', data: ts.power, color: '#fbbf24', area: true },
        { title: '扬程曲线 (m)', data: ts.head, color: '#a78bfa' },
      ];

      if (this.activeTab === '实时监测') {
        this.$nextTick(() => {
          setTimeout(() => this._renderCharts(), 200);
        });
      }
    },

    _generatePumpTimeSeries() {
      const POINTS = 96;
      const times = [];
      const flowData = [];
      const powerData = [];
      const headData = [];

      const baseFlow = 2.5;
      const basePower = 380;
      const baseHead = 32;

      const now = new Date();
      for (let i = 0; i < POINTS; i++) {
        const t = new Date(now.getTime() - (POINTS - 1 - i) * 15 * 60 * 1000);
        const hh = String(t.getHours()).padStart(2, '0');
        const mm = String(t.getMinutes()).padStart(2, '0');
        times.push(`${hh}:${mm}`);

        const phase = (i / POINTS) * Math.PI * 2;
        // 流量：早晚灌溉高峰
        const flowWave = Math.max(0, Math.sin(phase)) * 0.3 + 0.7;
        flowData.push(Math.round(baseFlow * flowWave * (0.92 + Math.random() * 0.08) * 100) / 100);

        // 功率：与流量正相关
        const powerRatio = flowData[i] / baseFlow;
        powerData.push(Math.round(basePower * powerRatio * (0.95 + Math.random() * 0.05) * 10) / 10);

        // 扬程：流量增大时扬程略降（水泵特性曲线）
        const headDrop = (flowData[i] - baseFlow * 0.7) * 2;
        headData.push(Math.round((baseHead - headDrop + (Math.random() - 0.5) * 0.4) * 100) / 100);
      }

      return { times, flow: flowData, power: powerData, head: headData };
    },

    _renderCharts() {
      this._destroyCharts();
      if (!this.tsData || !this.chartConfigs.length) return;

      this.chartConfigs.forEach((cfg, idx) => {
        const refKey = 'chart' + idx;
        const el = this.$refs[refKey];
        if (!el || (Array.isArray(el) && el.length === 0)) return;
        const dom = Array.isArray(el) ? el[0] : el;
        const w = dom.offsetWidth || dom.clientWidth || 380;
        const h = 130;
        dom.style.width = w + 'px';
        dom.style.height = h + 'px';

        let chart;
        try {
          chart = echarts.init(dom, null, { width: w, height: h, renderer: 'canvas' });
        } catch(e) {
          console.error('[pumpingInfo] echarts.init error', idx, e);
          return;
        }
        this.echartInstances.push(chart);

        const series = [{
          name: cfg.title,
          type: 'line',
          data: cfg.data,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2, color: cfg.color },
          itemStyle: { color: cfg.color },
          areaStyle: cfg.area ? {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: cfg.color + '40' },
              { offset: 1, color: cfg.color + '05' },
            ]),
          } : undefined,
        }];

        chart.setOption({
          grid: { left: 50, right: 15, top: 20, bottom: 25 },
          tooltip: { trigger: 'axis', textStyle: { fontSize: 11 } },
          legend: { show: false },
          xAxis: {
            type: 'category',
            data: this.tsData.times,
            axisLabel: { color: '#94a3b8', fontSize: 9, interval: 23 },
            axisLine: { lineStyle: { color: 'rgba(148,163,184,0.3)' } },
          },
          yAxis: {
            type: 'value',
            scale: true,
            axisLabel: { color: '#94a3b8', fontSize: 9 },
            splitLine: { lineStyle: { color: 'rgba(148,163,184,0.1)' } },
          },
          series,
        });
        setTimeout(() => { try { chart.resize(); } catch(e) {} }, 100);
      });
    },

    _destroyCharts() {
      this.echartInstances.forEach(c => { try { c.dispose(); } catch(e) {} });
      this.echartInstances = [];
    },

    closeClick() {
      this.$emit("infoClose");
    },
  },
};
</script>

<style scoped>
.pumping-info-panel {
  width: 45%;
  height: 65%;
  position: absolute;
  overflow-y: auto;
  max-height: 65%;
  left: 30%;
  top: 200px;
}

/* === 实时监测区域 === */
.ts-section {
  padding: 0 2px;
}

.ts-metrics-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.ts-metric-card {
  position: relative;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
  min-width: 100px;
}

.ts-metric-label {
  display: block;
  color: rgba(255, 255, 255, 0.52);
  font-size: 11px;
  margin-bottom: 4px;
}

.ts-metric-val-row {
  display: flex;
  align-items: baseline;
  gap: 3px;
}

.ts-metric-value {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
}

.ts-metric-unit {
  color: rgba(125, 211, 252, 0.76);
  font-size: 10px;
}

.ts-chart-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ts-chart-card {
  padding: 10px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.ts-chart-title {
  color: rgba(125, 211, 252, 0.76);
  font-size: 11px;
  margin-bottom: 4px;
}

.ts-chart-canvas {
  width: 100%;
  height: 130px;
  min-width: 300px;
  min-height: 130px;
}

/* === 状态颜色 === */
.st-red { color: #fca5a5; }
.st-yellow { color: #facc15; }
.st-green { color: #86efac; }
</style>
